(ns frontend.view.grid.persons-grid.events
  (:require
   [clojure.string :as string]
   [common.entity :as entity]
   [frontend.view.grid.persons-grid.db :as db]
   [frontend.view.grid.persons-grid.columns :as columns]
   [frontend.react.virtualized.scrollable-grid.common :as grid]
   [frontend.local-storage :as ls]
   [common.schema.registry :as registry]
   [common.localization :as loc]
   [common.util :as cu]
   [frontend.view.modals.events :as modals]
   [potpuri.core :refer [fn->]]
   [frontend.state.current-session :as session]
   [frontend.actions.named-view :as named-view-actions]
   [frontend.logging :as logging]
   [frontend.utils :as fu]
   [frontend.conn :as conn]
   [promesa.core :as p]
   [frontend.view.modals.confirmation.view :as confirmation]
   [reagent.core :as r]
   [common.intelligence.common :as intelligence]
   [frontend.react.virtualized.utils :as vu]
   [frontend.state.permissions :as f-permissions]
   [common.schema.entity-types :as entity-types]
   [frontend.view.modals.error-message.view :as error-message]
   [common.schema.entity-type-names :as entity-type-names]
   [frontend.view.grid.companies-grid.events :as companies-events]))

(defn ls-path
  [& rest]
  (concat
   [(:db/id @session/account)
    (:db/id @session/tenant)
    :contacts-view-settings]
   rest))

(defn person-current-employer?
  []
  (some #{:person/current-employer} (:tenant/fields-in-use @session/tenant)))

(defn precalculated-schema
  [recalculate?]
  (let [calculated @db/schema
        schema
        (cond
          (and (not recalculate?)
               calculated)
          calculated
          :else
          (->>
           (registry/get-schema @session/tenant-schema :entity.type/person)
           (remove (fn [[k v]]
                     (let [grid-visibility (->> v
                                                :field/default-visibility
                                                :grid)]
                       (or (nil? grid-visibility)
                           (= grid-visibility :editor.type/none)
                           (if (or (not= (:field/data-type v) :data.type/currency-symbol)
                                   (not (:field/secondary-field? v)))
                             (not (f-permissions/field-read-access? k :entity.type/person nil))
                             (not (f-permissions/field-read-access? (:field/related-fields v)
                                                                    :entity.type/person
                                                                    nil)))))))
           (sort-by (fn [[k v]]
                      (-> v
                          :field/translations
                          :en)))))]
    (when (or recalculate?
              (not calculated))
      (reset! db/schema schema))
    schema))

(defn person-and-company-schema
  [recalculate?]
  (let [calculated @db/schema
        schema
        (cond
          (and (not recalculate?)
               calculated)
          calculated
          :else
          (->>
           (registry/get-schema @session/tenant-schema :entity.type/person)
           (remove (fn [[k v]]
                     (let [grid-visibility (->> v
                                                :field/default-visibility
                                                :grid)]
                       (or (nil? grid-visibility)
                           (= grid-visibility :editor.type/none)
                           (if (not= (:field/data-type v) :data.type/currency-symbol)
                             (not (f-permissions/field-read-access? k :entity.type/person nil))
                             (not (f-permissions/field-read-access? (:field/related-fields v)
                                                                    :entity.type/person
                                                                    nil)))))))
           (sort-by (fn [[k v]]
                      (-> v
                          :field/translations
                          :en)))))
        company-schema (->> (companies-events/precalculated-schema recalculate?)
                            (map (fn [[k v]]
                                   [[:person/current-employer k] v]))
                            (remove (fn-> first (= [:person/current-employer :asset/name])))
                            (sort-by (fn [[_ v]]
                                       (-> v
                                           :field/translations
                                           :en)))
                            doall)
        schema (cond (person-current-employer?)
                     (concat schema company-schema)
                     :else
                     schema)]
    (when (or recalculate?
              (not calculated))
      (reset! db/schema schema))
    schema))

;;precalculate for performance reasons
(defn keys-and-schemas
  []
  (person-and-company-schema false))

(defn contacts-column-keys
  []
  (concat
   (when (session/is-admin?) [:db/id])
   (map first (keys-and-schemas))))

(defn column-tabs
  []
  (let [entity-types [:entity.type/person
                      :entity.type/company]]
    (->> entity-types
         (map (fn [entity-type]
                (let [entity-type-name (if (= entity-type :entity.type/company)
                                         (loc/tr :person/current-employer)
                                         (-> entity-type-names/entities
                                             (get entity-type)
                                             :field/translations
                                             :en))]
                  {:key   entity-type
                   :label (loc/tr :entity-type-fields [entity-type-name])}))))))

(defn resolve-tab
  [field]
  (let [ref-field? (and (vector? field)
                        (keyword? (first field))
                        (keyword? (second field)))
        [ref attr] (when ref-field?
                     field)]
    (cond (and (keyword? field)
               (not ref-field?))
          :entity.type/person
          ref-field?
          :entity.type/company
          :else :unknown)))

(def default-kanban-attributes
  [:person/notes :person/universal-email-address :person/universal-phone-number])

(defn visible-keys-and-schemas
  []
  (let [{:keys [attributes footer-1 footer-2]} @db/card-settings
        visible-attributes
        (set (concat @db/selected-columns
                     attributes
                     default-kanban-attributes
                     [footer-1 footer-2 (entity-types/type->owner-attribute :entity.type/person)]))]
    (->> (keys-and-schemas)
         (filter (fn [[k fs]]
                   (some #{k} visible-attributes))))))

(declare expanded? toggle-expand!)

(defn setup-args
  []
  (merge
   {:state db/contacts
    :entity-type :entity.type/person
    :modal-state db/modal-state
    :selected-columns-state db/selected-columns
    :ordered-columns-state db/ordered-columns
    :filters-state db/contact-filters
    :view-settings-state db/view-settings
    :column-widths-state db/column-widths
    :selected-pipeline nil
    :ls-path ls-path
    :kanban? true
    :kanban-args {:default-grouped-by  :person/contact-owner
                  :kanban-state        db/kanban
                  :grouped-by-state    db/grouped-by
                  :card-settings-state db/card-settings
                  ;;for long card view:
                  :default-attributes  default-kanban-attributes
                  :expanded-state      db/expanded
                  :sorted-kanban-entities-state db/sorted-kanban-entities
                  :lists-state         db/lists
                  :expanded?           expanded?
                  :toggle-expand!      toggle-expand!}

    :column-keys contacts-column-keys
    #_columns/column-keys
    :default-columns columns/default-columns
    :mandatory? columns/mandatory?
    :visible-keys-and-schemas-fn visible-keys-and-schemas}
   (when (person-current-employer?)
     {:column-tabs (column-tabs)
      :resolve-tab resolve-tab})))

(defn expanded?
  [entity-id]
  (let [card-mode (or (:card-mode @db/kanban)
                      :short-card)
        individual (get @db/expanded entity-id)]
    (or (and (= card-mode :short-card)
             individual)
        (and (= card-mode :long-card)
             (not individual)))))

(defn toggle-expand!
  [entity-id]
  (let [expanded? (get @db/expanded entity-id)]
    (if expanded?
      (swap! db/expanded dissoc entity-id)
      (swap! db/expanded assoc entity-id true))))

(defn resolve-column-key-universe
  []
  (grid/resolve-column-key-universe (setup-args)))

(defn resolve-column-keys
  []
  (grid/resolve-column-keys (setup-args)))

(defn cleanup
  []
  (when-let [tracker (:tracker-1 @db/contacts)]
    (r/dispose! tracker))
  (when-let [tracker (:tracker-2 @db/contacts)]
    (r/dispose! tracker))
  (when-let [tracker (:tracker-3 @db/contacts)]
    (r/dispose! tracker))

  (swap! db/contacts dissoc
    :loaded
    :schema
    :entities
    :entity-types
    :view-settings
    :sorted-grid-entities
    :sorted-kanban-entities
    :search
    :kanban
    :selected
    :grid-entities))

(defn toggle-quick-filters!
  []
  (swap! db/contacts update :show-quick-filters? not))

(defn system-views
  []
  [{:named-view/settings {:selected-columns columns/default-columns
                          :ordered-columns  columns/ordered-default-columns
                          :sort-by          :person/last-name
                          :sort-direction   "ASC"
                          :view-mode        :grid-mode}
    :named-view/name (loc/tr :all-contacts)
    :disable-delete? true
    :db/id "named-view-all-contacts"}
   {:named-view/settings {:selected-columns columns/default-columns
                          :ordered-columns  columns/ordered-default-columns
                          :view-mode        :grid-mode
                          :sort-by          :person/last-name
                          :sort-direction   "ASC"
                          :filters          {:person/contact-owner {:operand :operand/any-of
                                                                    :lower-bound-value
                                                                    #{(:db/id @session/account)}}}}
    :named-view/name (loc/tr :my-contacts)
    :disable-delete? true
    :db/id "named-view-my-contacts"}
   {:named-view/settings {:selected-columns columns/default-columns
                          :ordered-columns  columns/ordered-default-columns
                          :view-mode        :board-mode
                          :sort-by          :person/last-name
                          :sort-direction   "ASC"}
    :named-view/name (loc/tr :contacts-pipeline)
    :disable-delete? true
    :db/id "named-view-contacts-pipeline"}])

(defn system-views-by-id
  []
  (->> (system-views)
       (map (fn [v]
              [(:db/id v) v]))
       (into {})))

(defn named-views
  []
  (concat (system-views)
          (->> @session/all-named-views
               (cu/only-entity :named-view/view :contacts)
               (sort-by :common/created-on))))

(defn named-view-by-id
  [id]
  (or (get @session/entities id)
      (get (system-views-by-id) id)))

(defn switch-mode!
  [current-mode]
  (let [new-mode (if (= current-mode :grid-mode) :board-mode :grid-mode)]
    (swap! db/view-settings assoc :view-mode new-mode)
    (ls/update-in (ls-path) assoc :view-mode new-mode)))

(defn setup-column-state!
  [view-settings]
  (reset! db/view-settings view-settings)
  (when (nil? @db/selected-columns)
    (let [default-view-settings (->> (named-views)
                                     first
                                     :named-view/settings)]
      (reset! db/view-settings default-view-settings)
      (ls/set-in (ls-path) default-view-settings)))
  (grid/recalculate (assoc (setup-args) :re-sort? true)))

(defn load-view!
  [named-view] ;;named-view entity
  (let [{:keys [named-view/settings]} named-view]
    (ls/set-in (ls-path) settings)
    (setup-column-state! settings)))

(defn contacts-updates
  []
  (swap! db/contacts assoc
    :grid-entities
    (->> @session/all-persons
         (mapv intelligence/enrich-entity))))

(defn permission-updates
  []
  (let [current-permissions @session/current-permissions]
    (when current-permissions
      (precalculated-schema true))))

(defn trigger-recalculation
  []
  (let [permissions-trigger @db/schema
        entities-trigger @db/grid-entities]
    (when (or permissions-trigger entities-trigger)
      (fu/timeout 10
                  (fn []
                    (grid/recalculate (assoc (setup-args) :stale-sort? true))
                    (swap! db/contacts assoc :loaded true)
                    (deref db/contacts))))))

(defn setup
  [closed-atom?]
  (when-not @closed-atom?
    (if (empty? (ls/get-in (ls-path)))
      (load-view! (first (named-views)))
      (reset! db/view-settings (ls/get-in (ls-path))))
    (swap! db/contacts assoc
      :tracker-1 (r/track! contacts-updates)
      :tracker-2 (r/track! permission-updates)
      :tracker-3 (r/track! trigger-recalculation))
    (setup-column-state! (ls/get-in (ls-path)))))

(defn clear-ls-widths!
  [grid-store]
  (ls/set-in (ls-path :column-widths) {})
  (swap! db/view-settings assoc :column-widths {})
  (.setTimeout js/window #(vu/recompute-grid-sizes-with-state! grid-store) 100))

(defn save-current-view!
  [name]
  (let [settings @db/view-settings
        msg {:named-view/name     name
             :named-view/view     :contacts
             :named-view/settings settings}]
    (named-view-actions/*create-named-view->
     msg
     identity
     (fn [& a] ;;TODO show error message
       (logging/error "create-named-view failed" a)))))

(defn mass-delete!
  []
  (-> (conn/sendp! :person/mass-action
                   {:action     :delete-contacts
                    :person-ids (grid/get-selected-items (setup-args))})
      (p/then (fn [_args]
                (swap! db/contacts assoc :selected {})))
      (p/catch (fn [{:keys [error message] :as e}]
                 (logging/error "Unable to delete selected persons" e)
                 (let [show-error-message? (and (= error :bad-request)
                                                (string? message)
                                                (string/includes? message "map-profile"))]
                   (modals/open-modal!
                    error-message/modal-key
                    {:title          (loc/tr :attention)
                     :body-content   [:span (loc/tr :failed-to-delete-persons)
                                      (when show-error-message?
                                        [:<>
                                         [:br] [:br]
                                         [:span "(" message ")"]])]
                     :ok-button-text (loc/tr :ok)}))))))

(defn delete-selected-clicked
  [_]
  (let [person-ids (grid/get-selected-items (setup-args))
        cust-in (map session/customer-in person-ids)
        decision-participant-in (map session/person-is-decision-participant person-ids)]
    (cond
      (not-every? empty? cust-in)
      (modals/open-modal! error-message/modal-key
                          {:body-content   (loc/tr :customer-cannot-be-deleted)
                           :title          (loc/tr :attention)
                           :ok-button-text (loc/tr :back)})
      (not-every? empty? decision-participant-in)
      (let [participant-persons
            (->> decision-participant-in
                 (map-indexed (fn [index participant-entities]
                                (when (seq participant-entities)
                                  (let [person-id (nth person-ids index)
                                        person (session/get-entity person-id)]
                                    person))))
                 (remove nil?))
            person-names (->> participant-persons
                              (map entity/display-string)
                              (cu/join-with-and))
            body-text (if (= (count participant-persons) 1)
                        (loc/tr :decision-participant-person-cannot-be-deleted [person-names])
                        (loc/tr :decision-participant-persons-cannot-be-deleted [person-names]))]
        (modals/open-modal! error-message/modal-key
                            {:body-content   body-text
                             :title          (loc/tr :attention)
                             :ok-button-text (loc/tr :back)}))

      :else
      (modals/open-modal!
       confirmation/modal-key
       {:body          [:div
                        [:p (loc/tr :confirm-delete-selected)]]
        :confirm-text  (loc/tr :confirm-delete-selected-button)
        :checkbox-text (loc/tr :confirm-delete-selected-checkbox)
        :on-confirm    #(mass-delete!)}))))

(defn expand-all!
  []
  (swap! db/kanban assoc :card-mode :long-card)
  (reset! db/expanded {}))

(defn collapse-all!
  []
  (swap! db/kanban assoc :card-mode :short-card)
  (reset! db/expanded {}))

(defn open-card-settings!
  []
  (swap! db/kanban assoc :card-settings? true))

(defn close-card-settings!
  []
  (swap! db/kanban dissoc :card-settings?))
